/**
 * Picker arborescent du catalog réglementaire.
 *
 * Affiche : client → gamme → produit fini → variante (4 niveaux pliables).
 * Clic sur une variante → callback `onPick(produit, client, gamme, pays, langue, clientId)`
 * → le parent (RegulatoryImportModal) déclenche l'import.
 *
 * Chargement en DEUX temps (spécification FDS Studio du 16/09/2026) : la liste
 * des clients arrive légère, sans produits ; les gammes et produits d'un client
 * sont demandés au moment où on le déplie, par `onExpandClient(client.id)`.
 * L'ancien appel unique renvoyait tout le catalogue en plusieurs Mo — c'est lui
 * qui est resté en panne du 11 au 13 septembre.
 *
 * Champ de recherche en haut filtre les produits finis par nom, parmi les
 * clients DÉJÀ chargés : on ne peut pas chercher dans ce qu'on n'a pas encore
 * demandé, et charger tous les clients d'un coup pour une recherche nous
 * ramènerait au problème de départ (et à la limite de 30 appels par minute).
 */
import React from 'react';


function ChevronIcon({ open }) {
  return (
    <svg
      width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      style={{ transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}


function Attente({ texte = 'Chargement…' }) {
  return (
    <div className="py-3 flex items-center justify-center gap-2">
      <div className="w-3.5 h-3.5 border-2 border-orange-500/20 border-t-orange-500/70 rounded-full animate-spin" />
      <span className="text-[9px] font-mono uppercase tracking-widest text-white/40">{texte}</span>
    </div>
  );
}


function VarianteNode({ produit, variante, onPick, clientName, clientId, gammeName }) {
  // Niveau VARIANTE = une étiquette pour un pays, et une langue quand le pays en
  // a plusieurs (Belgique : fr / nl / de). Le CLIC sur la variante déclenche
  // DIRECTEMENT l'import — pas de feuille « nom du produit » répétée.
  const { cc, lang, libelle } = variante;
  const label = libelle || 'défaut';
  const infobulle = cc
    ? `Importer l'étiquette ${cc}${lang ? ` en ${lang}` : ''}`
    : 'Importer (variante par défaut)';
  return (
    <button
      onClick={() => onPick(produit, clientName, gammeName, cc, lang, clientId)}
      title={infobulle}
      className="w-full flex items-center justify-between gap-2 pl-12 pr-3 py-1.5 rounded-md text-left hover:bg-orange-500/15 transition-colors group"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-1.5 h-1.5 rounded-full bg-orange-400/60 group-hover:bg-orange-400 transition-colors shrink-0" />
        <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-orange-300 group-hover:text-white transition-colors">
          {label}
        </span>
      </div>
      <span className="text-[8px] font-mono uppercase tracking-widest text-white/25 group-hover:text-orange-400 transition-colors shrink-0">
        Importer →
      </span>
    </button>
  );
}


/**
 * Les variantes d'un produit : `labels_i18n[pays][langue]` d'abord — un pays à
 * une seule langue donne une entrée « FR », un pays à plusieurs langues une
 * entrée par langue « BE · fr » ; à défaut les clés de `labels` (ancien schéma) ;
 * à défaut une seule variante « défaut ».
 */
function variantesDe(produit) {
  const objet = (o) => (o && typeof o === 'object' && !Array.isArray(o)) ? o : null;
  const out = [];
  const i18n = objet(produit.labels_i18n);
  if (i18n) {
    for (const [cc, parLangue] of Object.entries(i18n)) {
      const langs = Object.keys(objet(parLangue) || {});
      if (langs.length <= 1) out.push({ cc, lang: langs[0] || null, libelle: cc });
      else for (const l of langs) out.push({ cc, lang: l, libelle: `${cc} · ${l}` });
    }
  }
  if (!out.length) {
    for (const cc of Object.keys(objet(produit.labels) || {})) out.push({ cc, lang: null, libelle: cc });
  }
  return out.length ? out : [{ cc: null, lang: null, libelle: null }];
}


function ProduitNode({ produit, onPick, clientName, clientId, gammeName, autoExpand }) {
  const variantes = React.useMemo(() => variantesDe(produit), [produit]);

  const [open, setOpen] = React.useState(!!autoExpand);
  // Cet effet ne savait que DÉPLIER : après une recherche puis un effacement du
  // champ, toutes les variantes restaient ouvertes sans retour possible à l'état
  // replié. Il suit maintenant `autoExpand` dans les deux sens ; comme il ne se
  // rejoue qu'au changement d'`autoExpand`, un pli manuel reste respecté.
  React.useEffect(() => { setOpen(!!autoExpand); }, [autoExpand]);

  return (
    <div>
      {/* Niveau PRODUIT — en-tête dépliable (n'importe pas directement) */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-1.5 pl-7 pr-2 py-1 rounded-md hover:bg-white/[0.04] text-left"
      >
        <span className="text-white/35"><ChevronIcon open={open} /></span>
        <span className="text-[11px] font-sans text-white/75 truncate">{produit.name}</span>
        {produit.nicotine_free === true ? (
          <span className="text-[8px] font-mono text-white/40 shrink-0">sans nicotine</span>
        ) : (produit.nicotine !== undefined && produit.nicotine !== null && (
          <span className="text-[8px] font-mono text-orange-500/60 shrink-0">{produit.nicotine} mg</span>
        ))}
        {produit.volume_ml !== undefined && produit.volume_ml !== null && (
          <span className="text-[8px] font-mono text-white/25 shrink-0">{produit.volume_ml} ml</span>
        )}
        <span className="text-[8px] font-mono text-white/25 ml-auto shrink-0">
          {variantes.length > 1 ? `${variantes.length} variantes` : '1 variante'}
        </span>
      </button>

      {/* Niveau VARIANTE — une entrée par pays, ou par pays · langue */}
      {open && (
        <div className="mt-0.5 flex flex-col">
          {variantes.map((v) => (
            <VarianteNode
              key={`${v.cc || '__def'}-${v.lang || ''}`}
              produit={produit}
              variante={v}
              onPick={onPick}
              clientName={clientName}
              clientId={clientId}
              gammeName={gammeName}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function GammeNode({ gamme, clientName, clientId, onPick, query, autoExpand }) {
  const filtered = React.useMemo(() => {
    const q = (query || '').toLowerCase().trim();
    const arr = gamme.produits_finis || [];
    if (!q) return arr;
    return arr.filter(p => (p.name || '').toLowerCase().includes(q));
  }, [gamme, query]);

  const searching = (query || '').trim().length > 0;
  const [open, setOpen] = React.useState(!!autoExpand);
  // Déplie pendant une recherche qui matche, et REPLIE quand la recherche est
  // effacée : l'ancienne version ne faisait que `setOpen(true)`, si bien qu'après
  // un effacement du champ toutes les gammes restaient ouvertes, sans autre moyen
  // de revenir à l'état replié que de refermer la fenêtre.
  React.useEffect(() => {
    setOpen(searching ? filtered.length > 0 : !!autoExpand);
  }, [searching, filtered.length, autoExpand]);

  if (filtered.length === 0) return null;

  return (
    <div className="ml-3">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-1.5 pl-2 pr-2 py-1 rounded-md hover:bg-white/[0.04] text-left"
      >
        <span className="text-white/40"><ChevronIcon open={open} /></span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/55">
          {gamme.name}
        </span>
        <span className="text-[8px] font-mono text-white/25 ml-auto">
          {filtered.length}
        </span>
      </button>
      {open && (
        <div className="mt-0.5 flex flex-col">
          {filtered.map((p, i) => (
            <ProduitNode key={i} produit={p} onPick={onPick} clientName={clientName} clientId={clientId} gammeName={gamme.name} autoExpand={!!query} />
          ))}
        </div>
      )}
    </div>
  );
}


function ClientNode({ client, etat, onPick, onExpandClient, query, autoExpand }) {
  // Un client est CHARGÉ quand il porte ses gammes. La liste légère ne les a
  // pas : le premier dépliage les demande.
  const charge = Array.isArray(client.gammes);
  const searching = (query || '').trim().length > 0;
  const [open, setOpen] = React.useState(!!autoExpand);
  // Même correction que sur les gammes : l'effet ne dépliait jamais dans l'autre
  // sens, donc les clients restaient tous ouverts une fois la recherche effacée.
  // On suit l'entrée/sortie de recherche, pas chaque frappe, pour ne pas rouvrir
  // un nœud que l'utilisateur vient de replier à la main pendant sa recherche.
  React.useEffect(() => {
    setOpen(searching ? true : !!autoExpand);
  }, [searching, autoExpand]);

  // Dépliage d'un client pas encore chargé → on demande son détail, une fois.
  // Pas de nouvel appel tant qu'un est en vol ou qu'un échec est affiché : le
  // bouton « Réessayer » est la seule voie de relance, pour ne pas mitrailler
  // FDS (30 appels par minute) sur un client qui répond mal.
  React.useEffect(() => {
    if (open && !charge && !etat?.loading && !etat?.error && client.id) onExpandClient(client.id);
  }, [open, charge, etat?.loading, etat?.error, client.id, onExpandClient]);

  // Combien de produits ? Chargé : recompté selon la recherche. Pas chargé : le
  // nombre annoncé par la liste — la recherche ne peut pas filtrer dedans, donc
  // un client non chargé reste visible pendant une recherche.
  const totalMatches = React.useMemo(() => {
    if (!charge) return client.produits_count ?? 0;
    const q = (query || '').toLowerCase().trim();
    if (!q) return (client.gammes || []).reduce((sum, g) => sum + (g.produits_finis?.length || 0), 0);
    return (client.gammes || []).reduce(
      (sum, g) => sum + (g.produits_finis || []).filter(p => (p.name || '').toLowerCase().includes(q)).length,
      0,
    );
  }, [charge, client, query]);

  if (totalMatches === 0 && !(searching && !charge)) return null;

  return (
    <div className="border-b border-white/[0.04] last:border-b-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-2 py-2 hover:bg-orange-500/[0.06] text-left"
      >
        <span className="text-orange-500/80"><ChevronIcon open={open} /></span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-orange-500/90">
          <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14h2M14 14h2M8 18h2M14 18h2"/>
        </svg>
        <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-orange-600">
          {client.name}
        </span>
        {client.country_code && (
          <span className="text-[8px] font-mono text-white/30">{client.country_code}</span>
        )}
        <span className="text-[9px] font-mono text-white/35 ml-auto">
          {totalMatches} produit{totalMatches > 1 ? 's' : ''}{!charge && searching ? ' · dépliez pour chercher' : ''}
        </span>
      </button>
      {open && (
        <div className="pb-1.5 pl-1">
          {!charge && etat?.loading && <Attente texte="Chargement du client…" />}
          {!charge && etat?.error && (
            <div className="mx-2 my-1.5 p-2 rounded-md bg-red-500/10 border border-red-500/40 text-[10px] font-mono text-red-400 flex items-center justify-between gap-2">
              <span className="min-w-0">⚠ {etat.error}</span>
              <button
                onClick={() => onExpandClient(client.id, { force: true })}
                className="shrink-0 px-2 py-1 rounded bg-orange-500 hover:bg-orange-600 text-white text-[9px] uppercase tracking-widest"
              >
                Réessayer
              </button>
            </div>
          )}
          {charge && (client.gammes || []).length === 0 && (
            <div className="px-3 py-2 text-[9px] font-mono uppercase tracking-widest text-white/30">Aucune gamme</div>
          )}
          {charge && (client.gammes || []).map((g, i) => (
            <GammeNode key={i} gamme={g} clientName={client.name} clientId={client.id} onPick={onPick} query={query} autoExpand={!!query} />
          ))}
        </div>
      )}
    </div>
  );
}


export default function RegulatoryCatalogPicker({ catalog, onPick, onExpandClient, clientStates = {}, loading, error }) {
  const [query, setQuery] = React.useState('');

  if (loading) {
    return (
      <div className="py-8 flex flex-col items-center gap-2">
        <div className="w-5 h-5 border-2 border-orange-500/20 border-t-orange-500/70 rounded-full animate-spin" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">Chargement…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-[11px] font-mono text-red-500">
        ⚠ {error}
      </div>
    );
  }

  const clients = catalog?.clients || [];

  return (
    <div className="flex flex-col gap-2">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Rechercher un produit fini…"
        className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[11px] font-sans text-white/90 placeholder-white/25 outline-none focus:border-orange-500/40 transition-colors"
      />
      <div className="max-h-[420px] overflow-y-auto bg-black/20 border border-white/5 rounded-lg shadow-inner">
        {clients.length === 0 && (
          <div className="p-4 text-center text-[10px] font-mono uppercase tracking-widest text-white/30">
            Catalogue vide
          </div>
        )}
        {clients.map((c, i) => (
          <ClientNode
            key={c.id || i}
            client={c}
            etat={clientStates[c.id]}
            onPick={onPick}
            onExpandClient={onExpandClient}
            query={query}
            autoExpand={clients.length === 1}
          />
        ))}
      </div>
      <p className="text-[9px] font-mono text-white/25 mt-1">
        Dépliez un client (ses produits se chargent à ce moment), puis un produit fini, et cliquez sur une variante pour importer son bloc réglementaire sur l'étiquette.
      </p>
    </div>
  );
}
